import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { OrganisationService } from '../../Component/Api/Organisation/OrganisationService';
import AppTextInput from '../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { MDButton } from '../../Component/Common/Components/MDButton';
import { Organisation } from '../../Component/Model/Dto/Organisation/Organisation';
import { CreateOrganisationValidationSchema } from './OrganisationValidationSchema';
import AppSelectList, { IDropDownOptions } from '../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { SmallLoading } from '../../Component/Common/Components/Loading/IntermediateLoading';
import { WSPScorecardService } from '../../Component/Api/WSPScorecard/WSPScorecardService';
import { HttpStatusCode } from '../../Component/Model/Enum/HttpStatusCode';
import { CustomDialogTitle } from '../Common/Custom/CustomDialogTitle';

interface IProps {
    open: boolean;
    organisationId: number;
    closeModal: () => void;
}

export const OrganisationModal = ({ open, organisationId, closeModal }: IProps) => {
    const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm({
        mode: 'all',
        resolver: yupResolver(CreateOrganisationValidationSchema)
    });

    const [SETATypesItems, setSETATypesItems] = useState<IDropDownOptions[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSetaAvailable, setIsSetaAvailble] = useState<boolean>(true);

    useEffect(() => {
        fetchOrganisation();
        fetchScorecardLicenses()
    }, [organisationId]);

    const fetchOrganisation = async () => {
        try {
            if (organisationId > 0) {
                let organisationResult = await OrganisationService.getOrganisation(organisationId);
                reset(organisationResult.data as Organisation);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const fetchScorecardLicenses = async () => {
        reset()
        try {
            const scorecardLicenses = await WSPScorecardService.getWSPScorecardLicenses()
            const updatedScorecardData = scorecardLicenses.data;
            if (updatedScorecardData.length > 0) {
                const setaTypesDropDown = updatedScorecardData.map(data => ({
                    id: data.setaTypeId,
                    value: data.scorecardTypeName
                }));
                setSETATypesItems(setaTypesDropDown)
            } else {
                setIsSetaAvailble(false);
            }


        } catch (error) {
            console.error("Error fetching Scorecard licenses:", error);
        } finally {
            setIsLoading(false)
        }
    };

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            if (organisationId > 0) {
                const organisationRequest: Organisation = {
                    organisationId: organisationId,
                    registrationName: data.registrationName,
                    registrationNumber: data.registrationNumber,
                    vatNumber: data.vatNumber,
                    tradeName: data.tradeName,
                    setaTypeId: data.setaTypeId !== '' ? data.setaTypeId : null
                }
                const result = await OrganisationService.updateOrganisation(organisationRequest);
                if (result != undefined) {
                    toast.success(`Organisation successfully updated`);
                    closeCreateModal();
                }
            } else {
                const organisationRequest: Organisation = {
                    registrationName: data.registrationName,
                    registrationNumber: data.registrationNumber,
                    vatNumber: data.vatNumber,
                    tradeName: data.tradeName,
                    setaTypeId: data.setaTypeId !== '' ? data.setaTypeId : null
                }
                const result = await OrganisationService.createOrganisation(organisationRequest);
                if (result != undefined) {
                    toast.success(`Organisation successfully created`);
                    closeCreateModal();
                }
            }

        } catch (error) {
            toast.error(`Could not create Organisation`);
            console.error("Error creating organisation:", error);
        }
    }

    const closeCreateModal = () => {
        closeModal();
        reset();
    }

    return (
        <Dialog disableEnforceFocus open={open} onClose={() => closeCreateModal()} aria-labelledby="form-dialog-title">
            <CustomDialogTitle id="addOrg-dialog-title" onClose={() => closeCreateModal()}>
                {organisationId > 0 ? 'Edit Organisation' : 'Capture Organisation'}
            </CustomDialogTitle>
            <DialogContent>
                <form id='addOrganisationForm' onSubmit={handleSubmit(handleDataSubmit)}>
                    {isLoading ? <SmallLoading></SmallLoading> :
                        <Grid container spacing={1} mt={1}>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput label='Registration Name *' name='registrationName' control={control} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput label='Registration Number' name='registrationNumber' control={control} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput label='VAT Number' name='vatNumber' control={control} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput label='Trade Name' name='tradeName' control={control} />
                            </Grid>
                            {isSetaAvailable ?
                                <Grid item xs={12}>
                                    <AppSelectList
                                        control={control}
                                        name="setaTypeId"
                                        label="SETA"
                                        items={SETATypesItems}
                                        size="small"
                                    />
                                </Grid>
                                :
                                <>
                                </>
                            }
                        </Grid>
                    }
                </form>
            </DialogContent>
            <DialogActions>                
                <LoadingButton loading={isSubmitting} variant='contained' form='addOrganisationForm' type="submit" color='primary'>
                    Save
                </LoadingButton>
                <MDButton variant='outlined' color='primary' onClick={() => closeCreateModal()}>Cancel</MDButton>
            </DialogActions>
        </Dialog>


    )
}
